import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/rootReducer";
import { setUserInformation } from "../../Login/slices/login.slice";
import { CheckinAndMaxAdvancedBooking, MetaData } from "../../Login/typings/login.types";
import LogoUploadDialog from "../logo-upload-dialog.component";
import { useRemoteSetMetadata } from "../../../hooks/Remote/Metadata/useRemoteSetMetadata";
import {
  filterEnabledPolicy,
  generateAdvancedBookingSettings,
  initialCheckinEnableGrp,
  initializeCheckinSettingAdvancedBookingTime
} from "../functions/company-card.component.functions";
import { StyledCompanyCardRoot } from "../admin-styled.component";
import { useRemoteDeleteCompany } from "../../../hooks/Remote/Companies/useRemoteDeleteCompany";
import { DeleteConfirmDialog } from "./delete-company-confirmation-dialog.component";
import { FileObject } from "../../FileDropzone/typings/FileObject.type";
import { CompanyCardSettingDialog } from "./SettingDialog/CompanyCard-Setting-Dialog.component";
import type { CheckinPolicyGroup } from "./CompanyCard.types";
import { toValidMuiColor } from "../../../utils/theme-color.utils";
import {
  buildIndependentBookingSettingsPayload,
  getVisibleIndependentBookingTypes
} from "./SettingDialog/company-card-independent-bookings.config";

export type { CheckinPolicyGroup } from "./CompanyCard.types";

export const COMPANY_CARD_COLOR_FALLBACK = "#6B7280";

interface Props {
  brandColor?: string | null;
  companyName?: string | null;
  logoUrl?: string | null;
  companyMetaData: MetaData;
  onConfirmSaveMeta: () => void;
}

export default function CompanyCard({
  brandColor = "",
  companyName = "",
  logoUrl = "",
  companyMetaData,
  onConfirmSaveMeta
}: Readonly<Props>) {
  const {
    id,
    hidePastBookings,
    showBookingStatistics,
    allowBookingCancellations,
    teamBookingAllowed,
    workplaceBookingEnabled,
    bookingsOnSundaysAllowed,
    bookingsOnSaturdaysAllowed,
    calendarSyncPossible,
    companyId,
    bookingNormHours,
    advancedBookingSettings,
    maxAllowedNoBookingTime,
    bookingAssistenceActive,
    checkinSettings,
    activateOfficeSafety,
    activateBookingServices,
    independentBookingSettings
  } = companyMetaData;

  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const userInformation = useSelector((state: RootState) => state.login.userInformation);

  const [expanded, setExpanded] = useState(false);
  const [logoPath, setLogoPath] = useState("");

  const [checkinEnabledGrp, setCheckinEnabledGrp] = useState<CheckinPolicyGroup>({
    workplace: false,
    parkingplace: false,
    electricchargingplace: false,
    conferencezone: false,
    vehicle: false,
    visitor: false
  });
  const [imageUpload, setImageUpload] = useState(false);
  const [logoFile, setLogoFile] = useState<FileObject[] | undefined>(undefined);
  const [homeOffice, setHomeOffice] = useState<{
    homeOfficeDays: [number, number];
    enabled: boolean;
    mandatory: boolean;
  }>({
    homeOfficeDays: [companyMetaData.minMobileWorkingDays, companyMetaData.maxMobileWorkingDays],
    enabled: companyMetaData.mobileWorkingEnabled,
    mandatory: companyMetaData.homeOfficePolicyObligatory
  });

  const basicSettState = {} as CheckinAndMaxAdvancedBooking;
  const [wpSetting, setWpSetting] = useState<CheckinAndMaxAdvancedBooking>(basicSettState);
  const [parkPlSetting, setParkPlSetting] = useState<CheckinAndMaxAdvancedBooking>(basicSettState);
  const [eChargePlSetting, setEChargePlSetting] =
    useState<CheckinAndMaxAdvancedBooking>(basicSettState);
  const [confZnSetting, setConfZnSetting] = useState<CheckinAndMaxAdvancedBooking>(basicSettState);
  const [vehicleSetting, setVehicleSetting] =
    useState<CheckinAndMaxAdvancedBooking>(basicSettState);
  const [visitorSetting, setVisitorSetting] =
    useState<CheckinAndMaxAdvancedBooking>(basicSettState);

  const [newValues, setNewValues] = useState<MetaData>({} as MetaData);

  const [isConfirmDeletionOpen, setIsConfirmDeletionOpen] = useState(false);

  const { mutateAsync: submitMetadataAsync } = useRemoteSetMetadata({ mode: "Edit" });
  const { mutateAsync: deleteCompany, status: deleteCompanyStatus } = useRemoteDeleteCompany();

  const [hasError, setHasError] = useState(false);
  const imageSrc = useMemo(() => {
    return logoUrl ?? newValues.logoPath ?? "";
  }, [logoUrl]);
  const showImage = Boolean(logoUrl ?? newValues.logoPath) && !hasError;
  const companyInitial = companyName?.charAt(0).toUpperCase();

  const basicCompColor = brandColor ?? theme.palette.primary.light;
  const companyColor = newValues.color ?? basicCompColor;
  const accentColor = toValidMuiColor(companyColor, COMPANY_CARD_COLOR_FALLBACK);

  const handleOpenSetting = () => {
    /* istanbul ignore next */
    setExpanded(!expanded);
  };

  const confirmDeleteCompany = () => {
    /* istanbul ignore next */
    if (!companyId) return;

    deleteCompany({ ids: [companyId] }).then(() => {
      setIsConfirmDeletionOpen(false);
      onConfirmSaveMeta();
    });
  };

  /* istanbul ignore next */
  const handleSaveNewValues = () => {
    const data = new FormData();

    data.append("currency", "EUR");
    data.append("hidePastBookings", String(newValues.hidePastBookings));
    data.append("showBookingStatistics", String(newValues.showBookingStatistics));
    data.append("activateOfficeSafety", String(newValues.activateOfficeSafety));
    data.append("activateBookingServices", String(newValues.activateBookingServices));
    data.append("allowBookingCancellations", String(newValues.allowBookingCancellations));
    data.append("teamBookingAllowed", String(newValues.teamBookingAllowed));
    data.append("workplaceBookingEnabled", String(newValues.workplaceBookingEnabled ?? true));
    data.append("bookingsOnSaturdaysAllowed", String(newValues.bookingsOnSaturdaysAllowed));
    data.append("bookingsOnSundaysAllowed", String(newValues.bookingsOnSundaysAllowed));
    data.append("calendarSyncPossible", String(newValues.calendarSyncPossible));
    data.append("bookingsAreAnonymous", "true");
    data.append("automatedWorkplaceBookingReflectionTime", "10");
    data.append("maxAdvancedZoneBookingLength", "10");
    data.append("companyId", JSON.stringify(companyId));
    data.append("timezone", "Europe/Berlin");
    data.append("color", toValidMuiColor(newValues.color));
    data.append("id", String(id ? Number(id) : 1));
    logoFile && data.append("file", logoFile[0].file);
    data.append("bookingNormHours", String(newValues.bookingNormHours));
    data.append("maxAllowedNoBookingTime", String(newValues.maxAllowedNoBookingTime));
    data.append("homeOfficePolicyObligatory", String(homeOffice.mandatory));
    data.append("mobileWorkingEnabled", String(homeOffice.enabled));
    data.append("minMobileWorkingDays", String(homeOffice.homeOfficeDays[0]));
    data.append("maxMobileWorkingDays", String(homeOffice.homeOfficeDays[1]));
    data.append("bookingAssistenceActive", String(newValues.bookingAssistenceActive));
    data.append(
      "checkinSettings",
      JSON.stringify(
        filterEnabledPolicy({
          checkinEnabledGrp,
          wpSetting,
          parkPlSetting,
          eChargePlSetting,
          confZnSetting,
          vehicleSetting,
          visitorSetting
        })
      )
    );

    data.append(
      "advancedBookingSettings",
      JSON.stringify(
        generateAdvancedBookingSettings(
          wpSetting,
          parkPlSetting,
          eChargePlSetting,
          confZnSetting,
          vehicleSetting,
          visitorSetting
        )
      )
    );

    const visibleIndependentBookingTypes = getVisibleIndependentBookingTypes({
      placeTypeIds: new Set(userInformation.placeTypes.map(pt => pt.id)),
      zoneTypeIds: new Set(userInformation.zoneTypes.map(zt => zt.id)),
      visitorManagementEnabled: userInformation.visitorManagementEnabled
    });
    const savedIndependentBookingSettings = buildIndependentBookingSettingsPayload(
      newValues.independentBookingSettings,
      visibleIndependentBookingTypes
    );
    data.append("independentBookingSettings", JSON.stringify(savedIndependentBookingSettings));

    const savedCheckinSettings = filterEnabledPolicy({
      checkinEnabledGrp,
      wpSetting,
      parkPlSetting,
      eChargePlSetting,
      confZnSetting,
      vehicleSetting,
      visitorSetting
    });
    const savedAdvancedBookingSettings = generateAdvancedBookingSettings(
      wpSetting,
      parkPlSetting,
      eChargePlSetting,
      confZnSetting,
      vehicleSetting,
      visitorSetting
    );

    submitMetadataAsync(data).then(() => {
      if (userInformation.companyId === companyId) {
        dispatch(
          setUserInformation({
            ...userInformation,
            company: {
              ...userInformation.company,
              meta: {
                ...userInformation.company.meta,
                ...newValues,
                checkinSettings: savedCheckinSettings,
                advancedBookingSettings: savedAdvancedBookingSettings,
                independentBookingSettings: savedIndependentBookingSettings
              }
            }
          })
        );
      }
      onConfirmSaveMeta();
      setExpanded(false);
    });
  };

  const setInitialValues = () => {
    const {
      initialWpSetting,
      initialParkSetting,
      initialElecSetting,
      initialConfSetting,
      initialVehicleSetting,
      initialVisitorSetting
    } = initializeCheckinSettingAdvancedBookingTime(checkinSettings, advancedBookingSettings);

    // set initial setting per booking type
    setWpSetting(initialWpSetting);
    setParkPlSetting(initialParkSetting);
    setEChargePlSetting(initialElecSetting);
    setConfZnSetting(initialConfSetting);
    setVehicleSetting(initialVehicleSetting);
    setVisitorSetting(initialVisitorSetting);

    // set initial checkin enabled
    setCheckinEnabledGrp(initialCheckinEnableGrp(checkinSettings));

    // set initial mobile working policy
    setHomeOffice({
      homeOfficeDays: [companyMetaData.minMobileWorkingDays, companyMetaData.maxMobileWorkingDays],
      enabled: companyMetaData.mobileWorkingEnabled,
      mandatory: companyMetaData.homeOfficePolicyObligatory
    });

    // update values
    setNewValues(prevState => ({
      ...prevState,
      color: brandColor,
      advancedBookingSettings,
      bookingNormHours,
      hidePastBookings,
      showBookingStatistics,
      allowBookingCancellations,
      teamBookingAllowed,
      workplaceBookingEnabled: workplaceBookingEnabled ?? true,
      bookingsOnSundaysAllowed,
      bookingsOnSaturdaysAllowed,
      calendarSyncPossible,
      companyId: companyId,
      logoPath: logoUrl,
      maxAllowedNoBookingTime,
      bookingAssistenceActive,
      checkinSettings,
      activateOfficeSafety,
      activateBookingServices,
      independentBookingSettings
    }));
  };

  useEffect(() => {
    setInitialValues();
  }, [
    hidePastBookings,
    showBookingStatistics,
    allowBookingCancellations,
    teamBookingAllowed,
    workplaceBookingEnabled,
    bookingsOnSaturdaysAllowed,
    bookingsOnSundaysAllowed,
    calendarSyncPossible,
    brandColor,
    advancedBookingSettings,
    bookingNormHours,
    logoUrl,
    companyId,
    maxAllowedNoBookingTime,
    bookingAssistenceActive,
    checkinSettings,
    activateOfficeSafety,
    activateBookingServices,
    independentBookingSettings
  ]);

  useEffect(() => {
    setHasError(false);
  }, [imageSrc]);

  return (
    <>
      <StyledCompanyCardRoot data-testid="company-card" sx={{ width: 400 }}>
        <Box
          data-testid="company-card-color-band"
          sx={{ height: 70, width: "100%", bgcolor: accentColor }}
        />

        <Box sx={{ px: "14px", pb: "14px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "18px",
              mb: "20px"
            }}
          >
            <Box
              data-testid="company-card-logo"
              sx={{
                width: 96,
                height: 96,
                borderRadius: "14px",
                bgcolor: "background.paper",
                border: theme => `0.5px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                position: "relative",
                top: -36,
                mb: "-36px",
                flexShrink: 0
              }}
            >
              {showImage ? (
                <Box
                  component="img"
                  src={imageSrc}
                  alt={`${companyName} logo`}
                  onError={() => setHasError(true)}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 600,
                    color: accentColor,
                    lineHeight: 1
                  }}
                >
                  {companyInitial}
                </Typography>
              )}
            </Box>

            <Typography
              component="h3"
              sx={{
                fontSize: 17,
                fontWeight: 500,
                color: "text.primary",
                pt: "8px",
                lineHeight: 1.3,
                minWidth: 0
              }}
            >
              {companyName}
            </Typography>
          </Box>

          <Box
            sx={{
              height: "0.5px",
              bgcolor: "divider",
              mb: "14px"
            }}
          />

          <Box
            data-testid="company-action-btn-grid"
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              data-testid="manage-company-btn"
              onClick={handleOpenSetting}
              aria-expanded={expanded}
              aria-label={t("Manage")}
            >
              {t("Manage")}
            </Button>
          </Box>
        </Box>
      </StyledCompanyCardRoot>

      <DeleteConfirmDialog
        companyName={companyName ?? t("company")}
        open={isConfirmDeletionOpen}
        onClose={() => setIsConfirmDeletionOpen(false)}
        handleConfirmDeletion={confirmDeleteCompany}
        deleteCompanyStatus={deleteCompanyStatus}
      />

      {/* company setting options */}
      <CompanyCardSettingDialog
        open={expanded}
        newValues={newValues}
        companyMetaData={companyMetaData}
        companyName={companyName}
        brandColor={brandColor}
        logoUrl={logoUrl}
        newSetting={{
          wpSetting,
          parkPlSetting,
          eChargePlSetting,
          confZnSetting,
          vehicleSetting,
          visitorSetting
        }}
        setNewSetting={{
          setWpSetting,
          setParkPlSetting,
          setEChargePlSetting,
          setConfZnSetting,
          setVehicleSetting,
          setVisitorSetting
        }}
        homeOffice={homeOffice}
        setHomeOffice={setHomeOffice}
        checkinEnabledGrp={checkinEnabledGrp}
        setCheckinEnabledGrp={setCheckinEnabledGrp}
        setNewValues={setNewValues}
        onSave={handleSaveNewValues}
        onClose={() => {
          setExpanded(false);
          setInitialValues();
        }}
        onEditLogo={() => setImageUpload(true)}
        logoFile={logoFile}
        onDeleteCompany={() => setIsConfirmDeletionOpen(true)}
      />

      {/* logo upload dialog */}
      <LogoUploadDialog
        logoFile={logoFile}
        setLogoFile={setLogoFile}
        imageUpload={imageUpload}
        setImageUpload={setImageUpload}
        setLogoPath={setLogoPath}
        logoPath={logoPath}
      />
    </>
  );
}
